import { MainLayout } from "@/components/layout/MainLayout"
import { PaginatedDocumentsBrowser } from "@/components/documents/PaginatedDocumentsBrowser"
import { PaginatedRelevantDocumentsList } from "@/components/documents/PaginatedRelevantDocumentsList"
import { FileUpload } from "@/components/upload/FileUpload"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { useParams, useNavigate } from "react-router-dom"
import { useState, useEffect, useRef } from "react"
import { useCaseById } from "@/hooks/useCaseById"
import { useUpdateCase } from "@/hooks/useUpdateCase"
import { useUpload } from "@/contexts/UploadContext"
import { ArrowLeft, Upload, Folder, Pencil, Search, X } from "lucide-react"
import { formatDistanceToNow } from "date-fns"
import { useDeleteDocument } from "@/hooks/useDeleteDocument"
import { useBatchDeleteDocuments } from "@/hooks/useBatchDeleteDocuments"
import { useCaseDocumentsPagination } from "@/hooks/useCaseDocumentsPagination"

import CMEReportsList from "@/components/cme-reports/CMEReportsList"

const CaseDetails = () => {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()

	const [showUpload, setShowUpload] = useState(false)
	const [isEditingDescription, setIsEditingDescription] = useState(false)
	const [currentDescription, setCurrentDescription] = useState("")
	
	// Search state
	const [searchQuery, setSearchQuery] = useState("")
	const [searchInput, setSearchInput] = useState("")
	const [includeIrrelevant, setIncludeIrrelevant] = useState(false)

	const { data: caseData, isLoading, error, refetch } = useCaseById(id)
	const updateCaseMutation = useUpdateCase(id)
	const deleteMutation = useDeleteDocument(id)
	const batchDeleteMutation = useBatchDeleteDocuments()
	const { uploadState } = useUpload()
	const { shouldPaginate, documentCount } = useCaseDocumentsPagination(caseData)

	// Track completed uploads to refresh case data
	const completedUploadsRef = useRef(new Set<string>())
	
	// Exit search mode when input becomes empty
	useEffect(() => {
		if (searchInput === "" && searchQuery !== "") {
			setSearchQuery("")
		}
	}, [searchInput, searchQuery])

	useEffect(() => {
		if (!caseData) {
			return
		}
		if (caseData.document_count === 0) {
			setShowUpload(true)
		}
		if (caseData.incident_description) {
			if (!isEditingDescription) {
				setCurrentDescription(caseData.incident_description)
			}
		} else if (!isEditingDescription) {
			setCurrentDescription("")
		}
	}, [caseData, isEditingDescription])

	// Monitor upload state for completed uploads for this case
	useEffect(() => {
		if (!id) return

		const completedTasksForThisCase = uploadState.tasks.filter(
			(task) => task.caseId === id && task.status === "completed"
		)

		// Check if any new uploads have completed since last check
		const newlyCompleted = completedTasksForThisCase.filter(
			(task) => !completedUploadsRef.current.has(task.id)
		)

		if (newlyCompleted.length > 0) {
			// Mark these uploads as seen
			newlyCompleted.forEach((task) => {
				completedUploadsRef.current.add(task.id)
			})

			// Refresh case data to show new documents
			refetch()
		}
	}, [uploadState.tasks, id, refetch])

	const handleSaveDescription = async () => {
		if (!id) return
		updateCaseMutation.mutate(
			{ incident_description: currentDescription },
			{
				onSuccess: () => {
					setIsEditingDescription(false)
				},
			}
		)
	}

	// Define the handlers here
	const handleDeleteDocument = async (documentId: string) => {
		await deleteMutation.mutateAsync(documentId, {
			onSuccess: () => {
				refetch()
			},
		})
	}

	const handleBatchDeleteDocuments = async (documentIds: string[]) => {
		if (!id) return // Ensure id is defined before using it
		await batchDeleteMutation.mutateAsync(
			{
				caseId: id,
				documentIds: documentIds,
			},
			{
				onSuccess: () => {
					refetch()
				},
			}
		)
	}
	
	// Search handlers
	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault()
		setSearchQuery(searchInput)
	}

	const clearSearch = () => {
		setSearchQuery("")
		setSearchInput("")
	}
	
	// Determine if we're in search mode
	const isSearchMode = searchQuery.length > 0

	if (isLoading) {
		return (
			<MainLayout>
				<div className="animate-pulse" aria-live="polite" aria-busy="true">
					<p className="sr-only">Loading case details...</p>
					<div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
					<div className="h-4 bg-gray-200 rounded w-1/4 mb-8"></div>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
						{[...Array(3)].map((_, i) => (
							<div key={i} className="h-40 bg-gray-200 rounded-lg"></div>
						))}
					</div>
				</div>
			</MainLayout>
		)
	}

	if (error) {
		return (
			<MainLayout>
				<div className="text-center py-12" role="alert" aria-live="assertive">
					<h2 className="text-xl font-medium text-destructive">
						Error loading case
					</h2>
					<p className="text-muted-foreground mt-2">{error.message}</p>
					<Button
						variant="secondary"
						className="mt-4"
						onClick={() => navigate("/cases")}
					>
						Back to Cases
					</Button>
				</div>
			</MainLayout>
		)
	}

	if (!caseData) {
		return (
			<MainLayout>
				<div className="text-center py-12">
					<h2 className="text-xl font-medium">Case not found</h2>
					<Button
						variant="secondary"
						className="mt-4"
						onClick={() => navigate("/cases")}
					>
						Back to Cases
					</Button>
				</div>
			</MainLayout>
		)
	}

	return (
		<MainLayout>
			<div className="mb-8">
				<button
					onClick={() => navigate("/cases")}
					className="flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
				>
					<ArrowLeft className="h-4 w-4 mr-1" />
					Back to Cases
				</button>

				<div className="flex items-start justify-between">
					<div className="flex-grow">
						<div className="flex items-center space-x-3">
							<div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
								<Folder className="h-5 w-5" />
							</div>
							<h1 className="text-2xl font-semibold text-foreground">
								{caseData.case_name}
							</h1>
						</div>
						<div className="ml-13 pl-13 mt-2 space-y-2">
							<p className="text-sm text-muted-foreground">
								Plaintiff:{" "}
								<span className="font-medium">
									{caseData.plaintiff_first_name} {caseData.plaintiff_last_name}
								</span>{" "}
								• Created{" "}
								{formatDistanceToNow(new Date(caseData.created_at), {
									addSuffix: true,
								})}
							</p>
							{isEditingDescription ? (
								<div className="space-y-2">
									<Textarea
										value={currentDescription}
										onChange={(e) => setCurrentDescription(e.target.value)}
										placeholder="Enter case description..."
										className="min-h-[80px] bg-white w-full"
									/>
									<div className="flex justify-end space-x-2">
										<Button
											variant="ghost"
											size="sm"
											onClick={() => {
												setIsEditingDescription(false)
												setCurrentDescription(
													caseData?.incident_description || ""
												)
											}}
										>
											Cancel
										</Button>
										<Button size="sm" onClick={handleSaveDescription}>
											Save
										</Button>
									</div>
								</div>
							) : (
								<div>
									<span className="text-sm font-semibold">
										Incident Description:
									</span>
									<div className="flex items-start space-x-2 justify-start">
										{caseData?.incident_description ? (
											<div className="text-sm text-muted-foreground">
												{caseData.incident_description}
											</div>
										) : (
											<span className="text-sm text-muted-foreground italic w-fit">
												No incident description provided.
											</span>
										)}
										<Button
											variant="ghost"
											size="icon"
											className="h-6 w-6 flex-shrink-0"
											onClick={() => setIsEditingDescription(true)}
											aria-label="Edit incident description"
										>
											<Pencil className="h-4 w-4" />
										</Button>
									</div>
								</div>
							)}
						</div>
					</div>
					<Button
						onClick={() => setShowUpload(!showUpload)}
						className={
							showUpload
								? "bg-secondary text-foreground hover:bg-secondary/90"
								: ""
						}
					>
						<Upload className="h-4 w-4 mr-2" />
						{showUpload ? "Cancel" : "Upload Documents"}
					</Button>
				</div>
			</div>

			{showUpload && (
				<div className="mb-8 p-6 border rounded-xl bg-background animate-slide-down">
					<h2 className="text-lg font-medium mb-4">Upload Documents</h2>
					<FileUpload caseId={id} className="max-w-full" />
				</div>
			)}

			{caseData.document_count > 0 && (
				<>
					{/* <div className="mb-8">
						<h3 className="text-lg font-medium mb-4">Case Record Reviews</h3>
						<RecordReviewsList caseId={id} />
					</div> */}
					<div className="mb-8">
						<h3 className="text-lg font-medium mb-4">CME Reports</h3>
						<CMEReportsList caseId={id} />
					</div>
					
					{/* Sticky Search Bar */}
					<div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b mb-6 -mx-6 px-6 py-4">
						<form onSubmit={handleSearch} className="flex gap-2">
							<div className="relative flex-1">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
								<Input
									placeholder="Search documents using AI..."
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									className="pl-10 pr-10"
								/>
								{searchInput && (
									<button
										type="button"
										onClick={() => setSearchInput("")}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
									>
										<X className="h-4 w-4" />
									</button>
								)}
							</div>
							<Button type="submit" disabled={!searchInput.trim()}>
								Search
							</Button>
							{isSearchMode && (
								<Button variant="ghost" onClick={clearSearch}>
									Clear
								</Button>
							)}
						</form>
						{isSearchMode && (
							<div className="mt-2 flex items-center gap-2">
								<Checkbox
									id="include-irrelevant"
									checked={includeIrrelevant}
									onCheckedChange={(checked) => 
										setIncludeIrrelevant(checked === true)
									}
								/>
								<label
									htmlFor="include-irrelevant"
									className="text-sm text-muted-foreground cursor-pointer"
								>
									Include documents marked as irrelevant
								</label>
							</div>
						)}
					</div>
				</>
			)}

			{caseData.document_count === 0 ? (
				<div className="text-center py-12 border rounded-xl border-dashed">
					<p className="text-muted-foreground">No documents added yet</p>
				</div>
			) : isSearchMode ? (
				/* Search Results - Use PaginatedDocumentsBrowser with search query */
				<PaginatedDocumentsBrowser
					caseId={id}
					onDeleteDocument={handleDeleteDocument}
					onBatchDeleteDocuments={handleBatchDeleteDocuments}
					searchQuery={searchQuery}
					includeIrrelevant={includeIrrelevant}
				/>
			) : (
				<>
					<div className="mb-8">
						<h3 className="text-lg font-medium mb-4">Relevant Documents</h3>
						<PaginatedRelevantDocumentsList
							caseId={id}
							onDeleteDocument={handleDeleteDocument}
							onBatchDeleteDocuments={handleBatchDeleteDocuments}
						/>
					</div>
					<div>
						<h3 className="text-lg font-medium mb-4">All Documents {documentCount > 0 && `(${documentCount} total)`}</h3>
						<PaginatedDocumentsBrowser
							caseId={id}
							onDeleteDocument={handleDeleteDocument}
							onBatchDeleteDocuments={handleBatchDeleteDocuments}
						/>
					</div>
				</>
			)}
		</MainLayout>
	)
}

export default CaseDetails
